import { baseApi } from "./baseApi";

export type PaymentStatus = "Pending" | "Received" | "Verified";
export type PaymentMethod = "Cash" | "BankTransfer" | "Cheque" | "UPI" | "NEFT" | "RTGS";

export interface PaymentRecord {
  id: number;
  customerName: string;
  projectId?: number;
  projectName?: string;
  amount: number;
  paymentDate: string;   // YYYY-MM-DD
  paymentMethod: PaymentMethod;
  referenceNumber?: string;
  notes?: string;
  invoiceUrl?: string;
  invoiceFileName?: string;
  status: PaymentStatus;
  createdAt: string;
}

export interface CreatePaymentPayload {
  customerName: string;
  projectId?: number;
  projectName?: string;
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  referenceNumber?: string;
  notes?: string;
  status?: PaymentStatus;
}

export type UpdatePaymentPayload = CreatePaymentPayload;

interface PaymentApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  errors: string[];
}

const unwrap = <T>(res: PaymentApiResponse<T> | unknown): T => {
  const r = res as PaymentApiResponse<T>;
  return (r?.data ?? res) as T;
};

const normalizePayment = (raw: Record<string, any>): PaymentRecord => ({
  id: Number(raw.id ?? 0),
  customerName: String(raw.customerName ?? raw.customer ?? ""),
  projectId: raw.projectId != null ? Number(raw.projectId) : undefined,
  projectName: raw.projectName ? String(raw.projectName) : undefined,
  amount: Number(raw.amount ?? 0),
  paymentDate: String(raw.paymentDate ?? ""),
  paymentMethod: (raw.paymentMethod ?? "Cash") as PaymentMethod,
  referenceNumber: raw.referenceNumber ? String(raw.referenceNumber) : undefined,
  notes: raw.notes ? String(raw.notes) : undefined,
  invoiceUrl: raw.invoiceUrl ? String(raw.invoiceUrl) : undefined,
  invoiceFileName: raw.invoiceFileName ? String(raw.invoiceFileName) : undefined,
  status: (raw.status ?? "Pending") as PaymentStatus,
  createdAt: String(raw.createdAt ?? ""),
});

export const paymentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchPayments: builder.query<PaymentRecord[], void>({
      query: () => ({ url: "/Payment", method: "GET" }),
      transformResponse: (res: PaymentApiResponse<unknown> | unknown) => {
        const data = unwrap(res);
        if (Array.isArray(data)) return data.map((item) => normalizePayment(item as Record<string, any>));
        return [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Payment" as const, id })),
              { type: "Payment" as const, id: "LIST" },
            ]
          : [{ type: "Payment" as const, id: "LIST" }],
    }),

    fetchPaymentById: builder.query<PaymentRecord, number>({
      query: (id) => ({ url: `/Payment/${id}`, method: "GET" }),
      transformResponse: (res: PaymentApiResponse<unknown> | unknown) =>
        normalizePayment((unwrap(res) ?? {}) as Record<string, any>),
      providesTags: (_r, _e, id) => [{ type: "Payment" as const, id }],
    }),

    createPayment: builder.mutation<PaymentRecord, CreatePaymentPayload>({
      query: (body) => ({ url: "/Payment", method: "POST", body }),
      transformResponse: (res: PaymentApiResponse<unknown> | unknown) =>
        normalizePayment((unwrap(res) ?? {}) as Record<string, any>),
      invalidatesTags: [{ type: "Payment" as const, id: "LIST" }],
    }),

    updatePayment: builder.mutation<PaymentRecord, { id: number } & UpdatePaymentPayload>({
      query: ({ id, ...body }) => ({ url: `/Payment/${id}`, method: "PUT", body }),
      transformResponse: (res: PaymentApiResponse<unknown> | unknown) =>
        normalizePayment((unwrap(res) ?? {}) as Record<string, any>),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Payment" as const, id },
        { type: "Payment" as const, id: "LIST" },
      ],
    }),

    updatePaymentStatus: builder.mutation<PaymentRecord, { id: number; status: PaymentStatus }>({
      query: ({ id, status }) => ({
        url: `/Payment/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      transformResponse: (res: PaymentApiResponse<unknown> | unknown) =>
        normalizePayment((unwrap(res) ?? {}) as Record<string, any>),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Payment" as const, id },
        { type: "Payment" as const, id: "LIST" },
      ],
    }),

    uploadPaymentInvoice: builder.mutation<PaymentRecord, { id: number; file: File }>({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return { url: `/Payment/${id}/invoice`, method: "POST", body: formData };
      },
      transformResponse: (res: PaymentApiResponse<unknown> | unknown) =>
        normalizePayment((unwrap(res) ?? {}) as Record<string, any>),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Payment" as const, id },
        { type: "Payment" as const, id: "LIST" },
      ],
    }),

    deletePayment: builder.mutation<void, number>({
      query: (id) => ({ url: `/Payment/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Payment" as const, id },
        { type: "Payment" as const, id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useFetchPaymentsQuery,
  useFetchPaymentByIdQuery,
  useCreatePaymentMutation,
  useUpdatePaymentMutation,
  useUpdatePaymentStatusMutation,
  useUploadPaymentInvoiceMutation,
  useDeletePaymentMutation,
} = paymentsApi;
